(() => {
  const bootstrapSelector = '[data-aos][data-aos-once="false"]';
  const managedSelector = '[data-aos][data-global-aos-replay="true"]';

  let entries = [];
  let refreshRaf = null;
  let resetRaf = null;
  const resetStyleCache = new WeakMap();

  function refreshAos() {
    if (!window.AOS) return;
    if (typeof window.AOS.refreshHard === 'function') {
      try { window.AOS.refreshHard(); } catch (_) {}
      return;
    }
    if (typeof window.AOS.refresh === 'function') {
      try { window.AOS.refresh(); } catch (_) {}
    }
  }

  function getTopEdge() {
    if (window.__themeMotionUtils && typeof window.__themeMotionUtils.getActiveViewportTopEdge === 'function') {
      return window.__themeMotionUtils.getActiveViewportTopEdge(0);
    }
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    return vh > 0 ? Math.min(vh - 1, 0) : 0;
  }

  function applyInstantResetStyles(target) {
    if (!target) return;
    if (!resetStyleCache.has(target)) {
      resetStyleCache.set(target, {
        transitionDuration: target.style.transitionDuration || '',
        transitionDelay: target.style.transitionDelay || '',
        animationDuration: target.style.animationDuration || ''
      });
    }
    target.style.transitionDuration = '0ms';
    target.style.transitionDelay = '0ms';
    target.style.animationDuration = '0ms';
  }

  function restoreInstantResetStyles(targets) {
    requestAnimationFrame(() => {
      targets.forEach((target) => {
        const cached = resetStyleCache.get(target);
        if (!cached) return;
        if (cached.transitionDuration) target.style.transitionDuration = cached.transitionDuration;
        else target.style.removeProperty('transition-duration');
        if (cached.transitionDelay) target.style.transitionDelay = cached.transitionDelay;
        else target.style.removeProperty('transition-delay');
        if (cached.animationDuration) target.style.animationDuration = cached.animationDuration;
        else target.style.removeProperty('animation-duration');
        resetStyleCache.delete(target);
      });
    });
  }

  function normalizeTarget(node) {
    if (!node || !node.isConnected) return null;
    if (!node.getAttribute('data-aos')) return null;

    if (node.getAttribute('data-aos-once') === 'false' && !node.hasAttribute('data-global-aos-replay')) {
      node.setAttribute('data-global-aos-replay', 'true');
      node.setAttribute('data-aos-once', 'true');
      return node;
    }

    if (node.getAttribute('data-global-aos-replay') === 'true') {
      if (!node.getAttribute('data-aos-once') || node.getAttribute('data-aos-once') !== 'true') {
        node.setAttribute('data-aos-once', 'true');
      }
      return node;
    }

    return null;
  }

  function getResetRoot(sectionRoot) {
    if (!sectionRoot) return null;
    const sectionId = sectionRoot.id ? sectionRoot.id.replace(/^shopify-section-/, '') : '';
    if (sectionId) {
      const wrapper = sectionRoot.querySelector('.customstyle' + sectionId);
      if (wrapper) return wrapper;
    }
    return sectionRoot;
  }

  function buildEntries() {
    const sectionMap = new Map();
    const nodes = Array.from(document.querySelectorAll(bootstrapSelector + ',' + managedSelector));

    nodes.forEach((node) => {
      const target = normalizeTarget(node);
      if (!target) return;
      const sectionRoot = target.closest('[id^="shopify-section-"]');
      if (!sectionRoot) return;

      const key = sectionRoot.id || String(sectionMap.size);
      if (!sectionMap.has(key)) {
        sectionMap.set(key, {
          sectionRoot,
          resetRoot: getResetRoot(sectionRoot),
          targets: []
        });
      }

      sectionMap.get(key).targets.push(target);
    });

    entries = Array.from(sectionMap.values()).filter((entry) => (
      entry.sectionRoot &&
      entry.sectionRoot.isConnected &&
      entry.resetRoot &&
      entry.resetRoot.isConnected &&
      entry.targets.length
    ));

    refreshAos();
    scheduleReset();
  }

  function syncReplayResetState() {
    resetRaf = null;

    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    if (vh <= 0) return;
    const topEdge = getTopEdge();
    let needsRefresh = false;

    entries = entries.filter((entry) => (
      entry &&
      entry.sectionRoot &&
      entry.sectionRoot.isConnected &&
      entry.resetRoot &&
      entry.resetRoot.isConnected &&
      entry.targets.some((target) => target && target.isConnected && target.getAttribute('data-global-aos-replay') === 'true')
    ));

    entries.forEach((entry) => {
      const rect = entry.resetRoot.getBoundingClientRect();
      if (rect.bottom > topEdge && rect.top < vh) return;

      const animatedTargets = entry.targets.filter((target) => (
        target &&
        target.isConnected &&
        target.classList.contains('aos-animate')
      ));

      if (!animatedTargets.length) return;

      animatedTargets.forEach(applyInstantResetStyles);
      animatedTargets.forEach((target) => target.classList.remove('aos-animate'));
      restoreInstantResetStyles(animatedTargets);
      needsRefresh = true;
    });

    if (needsRefresh) refreshAos();
  }

  function scheduleRefresh() {
    if (refreshRaf) return;
    refreshRaf = requestAnimationFrame(() => {
      refreshRaf = null;
      buildEntries();
    });
  }

  function scheduleReset() {
    if (resetRaf) return;
    resetRaf = requestAnimationFrame(syncReplayResetState);
  }

  function handleViewportChange() {
    if (window.__themeMotionUtils && typeof window.__themeMotionUtils.invalidateHeaderCandidates === 'function') {
      window.__themeMotionUtils.invalidateHeaderCandidates();
    }
    scheduleRefresh();
  }

  function bindEvents() {
    window.addEventListener('scroll', scheduleReset, { passive: true });
    window.addEventListener('resize', handleViewportChange, { passive: true });
    window.addEventListener('load', handleViewportChange, { once: true });
    window.addEventListener('pageshow', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange, { passive: true });
    document.addEventListener('shopify:section:load', handleViewportChange);
    document.addEventListener('shopify:section:unload', handleViewportChange);

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange, { passive: true });
    }
  }

  function init() {
    if (window.__themeGlobalAosReplayManager) {
      window.__themeGlobalAosReplayManager.refresh();
      return;
    }

    bindEvents();
    window.__themeGlobalAosReplayManager = {
      refresh() {
        handleViewportChange();
      }
    };
    scheduleRefresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
